import React from 'react';
import classnames from 'classnames';
import { Link } from 'found';
import { Navbar, Nav } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../reducers/user.selectors';
import { routes, onboardingHints } from '../../../constants';
import Icon from '../../Icon/Icon';
import SearchBarTop from '../../SearchBarTop/SearchBarTop';
import TopBarMenu from './TopBarMenus/TopBarMenu';
import './topBar.scss';
import TopBarMobileMenu from './TopBarMenus/TopBarMobileMenu';
import WithTooltip from '../../WithTooltip';
import { useTranslation } from 'react-i18next';
import { useAmplitudeExperiment } from '../../../utils/ampli';
import config from 'config';
import { Theme, useMediaQuery } from '@mui/material';
import { OnboardingHint } from '../../OnboardingHint';
import UpgradeCTA from './UpgradeCTA';

interface TopBarProps {
  hideSearch?: boolean;
  hideUpgradeToPremiumButton?: boolean;
  hideUpgradeToSchoolLicenceButton?: boolean;
  hideOpenCollections?: boolean;
  hidePinnedDocuments?: boolean;
  hideMenu?: boolean;
  hideHome?: boolean;
  isPublicPage?: boolean;  // TODO: Clean up (RED-26)
}

const TopBar: React.FC<TopBarProps> = (props) => {
  const user = useSelector(selectUser);
  const { t } = useTranslation();
  
  const {
    variant: lernsetVariant,
    isReady: isClientReady,
  } = useAmplitudeExperiment(user, config.amplitudeExperiments.enableLearnsetsFeature);
  
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('mobile'));
  const isLernsetsActive = isClientReady && lernsetVariant === 'on';

  const showLernsetsCTA = isLernsetsActive && !props.hideMenu && !isMobile && user?.hasDocumentLevelAccess;
  const showCoursesCTA = !props.hideMenu && !isMobile;
  const showUpgradeCTA = 
    !(props.hideUpgradeToPremiumButton || props.hideUpgradeToSchoolLicenceButton || props.hideMenu);

  const lernsetsTopBarCTA = () => {
    return <div className='top-bar__icon-container'>
      <TopBarIconLink
        link={`/${routes.learnsetsDashboard.root}`}
        tooltip={'Lernsets'}
      >
        <span className='top-bar__icon top-bar__premium-link'>
          {'Lernsets'}
          <span
            data-testid='top-bar__lernsets'
            className='top-bar__premium-pill'
          >
            {'BETA'}
          </span>
        </span>
      </TopBarIconLink>
    </div>;
  };

  const coursesTopBarCTA = () => {
    return <OnboardingHint
      id={onboardingHints.courseOnboardingHint}
      title={user?.isDocumentUser ? t('Over 500 courses') : t('All Schulflix courses')}
      description={user?.isDocumentUser ?
        t('You can now find practical courses for your further training under meineFortbildung.') :
        t('You can find the course catalogue as well as your courses and certificates under meineFortbildung.')
      }
      placement='bottom-end'
      isNew={true}
      disablePortal={true}
      maxWidth={380}
    >
      <div className='top-bar__icon-container'>
        <TopBarIconLink
          link={`/${routes.courseCatalog.root}`}
          tooltip={t('Courses')}
        >
          <span className='top-bar__icon top-bar__premium-link'>
            {t('Courses')}
            <span
              data-testid='top-bar__courses'
              className='top-bar__premium-pill'
            >
              {'BETA'}
            </span>
          </span>
        </TopBarIconLink>
      </div>
    </OnboardingHint>;
  };

  return (
    <Navbar className={
      classnames(
        'top-bar__container',
        { 'top-bar__container--isPublicPage': props.isPublicPage }
      )
    }
    data-testid='top-bar__container'
    sticky='top'
    >
      {user && <TopBarMobileMenu hideWalkInItems={user.isWalkIn} />}
      {!props.hideHome &&
        <TopBarIconLink
          link={user?.isWalkIn ? `/${routes.search.root}` : `/${routes.dashboard.root}`}
          showOnMobile={!user}
          tooltip={t('Go to dashboard')}
        >
          <Icon icon='home' size='lg' className='top-bar__icon' />
        </TopBarIconLink>
      }
      {
        user &&
        <>
          <Nav navbar={true} data-testid='top-bar__nav' className='align-items-center flex-grow-1 justify-content-end'>
            {
              props.hideSearch
                ? null
                : <SearchBarTop className='mr-md-4' />
            }
            { showCoursesCTA && coursesTopBarCTA() }
            { showLernsetsCTA && lernsetsTopBarCTA() }
            { showUpgradeCTA && <UpgradeCTA shouldRenderSeparator={showLernsetsCTA || showCoursesCTA} /> }
            {
              props.hideOpenCollections
                ? null
                :
                <TopBarIconLink
                  hideLink={user.isWalkIn}
                  link={`/${routes.collections.root}`}
                  tooltip={t('Open Collections')}
                >
                  <Icon icon={'collection'} size='lg' className='top-bar__icon' />
                </TopBarIconLink>
            }
            {
              props.hidePinnedDocuments
                ? null
                :
                <TopBarIconLink
                  hideLink={user.isWalkIn}
                  link={`/${routes.pinned.root}`}
                  tooltip={t('Open Pinned Documents')}
                >
                  <Icon icon={'star-outline'} size='lg' className='top-bar__icon' />
                </TopBarIconLink>
            }
            {
              props.hideMenu
                ? null
                :
                <div className='top-bar__icon-container d-none d-md-flex' data-testid='user-settings-dropdown-button'>
                  <TopBarMenu hideWalkInItems={user.isWalkIn} />
                </div>
            }
          </Nav>
        </>
      }
    </Navbar>
  );
};

interface TopBarIconLinkProps {
  hideLink?: boolean;
  showOnMobile?: boolean;
  link: string;
  tooltip: string;
}

export const TopBarIconLink: React.FC<TopBarIconLinkProps> = (props) => {
  if (props.hideLink) return null;
  return (
    <Nav.Item className={classnames('top-bar__icon-container', { 'd-none d-md-flex': !props.showOnMobile })}>
      <WithTooltip text={props.tooltip} placement='bottom'>
        <Link
          className='top-bar__icon__link'
          to={props.link}>
          {props.children}
        </Link>
      </WithTooltip>
    </Nav.Item>
  );
};

export default TopBar;
